import { useStateValue } from "frontend/state";
import { useSetAtom } from "jotai";
import { PostHog, usePostHog } from "posthog-js/react";
import { useRef, useEffect } from "react";
import { publicFeatureFlagsLoadedAtom, authenticatedFeatureFlagsLoadedAtom } from "./state";
import { AB_TEST_KEY } from "./flags";
import { FRONTEND_VERSION } from "utils/version";
import { User } from "shared/datamodel/schemas";
import { getSafeUrlPathname } from "utils/url-utils";
import { prepareUserObjectForAnalytics } from "utils/users-utils";
import { registerMixpanelPropertiesOnce } from "utils/analytic-utils";

const FEATURE_FLAGS_TIMEOUT = 5000;

function setFirstTimeUserInitialProperties(user: User, posthogObject: PostHog) {
  const storedPersonProps = posthogObject.get_property("$stored_person_properties");

  if (!storedPersonProps?.$initial_pathname) {
    const initialPersonInfo = posthogObject.get_property("$initial_person_info")?.u || window.location.hostname;
    const propertiesForFeatureFlags = {
      ...user,
      $initial_pathname: getSafeUrlPathname(initialPersonInfo),
    };

    posthogObject.setPersonPropertiesForFlags(propertiesForFeatureFlags);
  }
}

export default function FeatureFlagsListener() {
  const [{ user }] = useStateValue();
  const posthog = usePostHog();
  const isUserIdentified = useRef(false);
  const publicFlagsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const authenticatedFlagsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setPublicFeatureFlagsLoaded = useSetAtom(publicFeatureFlagsLoadedAtom);
  const setAuthenticatedFeatureFlagsLoaded = useSetAtom(authenticatedFeatureFlagsLoadedAtom);

  useEffect(() => {
    // Set up timeout for public feature flags
    publicFlagsTimeoutRef.current = setTimeout(() => {
      setPublicFeatureFlagsLoaded(true);
    }, FEATURE_FLAGS_TIMEOUT);

    posthog.onFeatureFlags((_flags, variants) => {
      if (publicFlagsTimeoutRef.current) {
        clearTimeout(publicFlagsTimeoutRef.current);
        publicFlagsTimeoutRef.current = null;
      }

      setPublicFeatureFlagsLoaded(true);

      if (isUserIdentified.current) {
        // Clear authenticated timeout if it exists
        if (authenticatedFlagsTimeoutRef.current) {
          clearTimeout(authenticatedFlagsTimeoutRef.current);
          authenticatedFlagsTimeoutRef.current = null;
        }
        setAuthenticatedFeatureFlagsLoaded(true);
      }

      for (const [key, value] of Object.entries(variants)) {
        if (key.includes(AB_TEST_KEY)) {
          registerMixpanelPropertiesOnce({ [key]: value });
        }
      }
    });

    // Cleanup function
    return () => {
      if (publicFlagsTimeoutRef.current) {
        clearTimeout(publicFlagsTimeoutRef.current);
      }
      if (authenticatedFlagsTimeoutRef.current) {
        clearTimeout(authenticatedFlagsTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog]);

  useEffect(() => {
    if (user && posthog) {
      setFirstTimeUserInitialProperties(user, posthog);
      posthog.identify(user.id.toString(), prepareUserObjectForAnalytics(user));
      posthog.group("account", `id::${user?.accountId || user.account?.id || ""}`, {});
      posthog.group("version", `version::${FRONTEND_VERSION}`, {});
      isUserIdentified.current = true;

      // Set up timeout for authenticated feature flags
      if (!authenticatedFlagsTimeoutRef.current) {
        authenticatedFlagsTimeoutRef.current = setTimeout(() => {
          setAuthenticatedFeatureFlagsLoaded(true);
        }, FEATURE_FLAGS_TIMEOUT);
      }
    }
  }, [user, posthog, setAuthenticatedFeatureFlagsLoaded]);

  return null;
}
